import { useAppDispatch, useAppSelector } from "@/app/hooks";
import { TableContainer } from "@/components";
import { getCampaignTypes } from "@/features";
import { ICampaignType } from "@/types";
import { ColumnDef } from "@tanstack/react-table";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  FormGroup,
  Input,
  Label,
  Row,
} from "reactstrap";
import { CreateOrEditCampaignType } from "../components/CreateOrEditCampaignType";
import { DeleteCampaignType } from "../components/DeleteCampagnType";
import { FilterProps } from "../types";

export const CampaignTypeTab = () => {
  const { campaignTypeList } = useAppSelector((state) => state.campaign);
  const dispatch = useAppDispatch();
  const [data, setData] = useState<ICampaignType[]>([]);
  const [filter, setFilter] = useState<FilterProps>({
    campaignType: "",
  });
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isDelete, setIsDelete] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const columns = useMemo<ColumnDef<ICampaignType>[]>(
    () => [
      {
        header: "Campaign Type",
        accessorKey: "name",
        disableFilters: true,
        enableColumnFilter: false,
      },
      {
        header: "Position",
        accessorKey: "position",
        disableFilters: true,
        enableColumnFilter: false,
      },
      {
        header: "Action",
        disableFilters: true,
        enableColumnFilter: false,
        cell: ({ row }) => {
          return (
            <div>
              <Button
                color="primary"
                className="btn-sm"
                style={{ minWidth: 58 }}
                onClick={() => {
                  setSelectedId(row.original.id as number);
                  setIsOpen(true);
                }}
              >
                Edit
              </Button>
              <Button
                color="danger"
                className="btn-sm ms-2"
                style={{ minWidth: 58 }}
                onClick={() => {
                  setSelectedId(row.original.id as number);
                  setIsDelete(true);
                }}
              >
                Delete
              </Button>
            </div>
          );
        },
      },
    ],
    [],
  );

  const getData = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await dispatch(getCampaignTypes()).unwrap();
      setData(result);
    } catch (error) {
      console.error("error:", error);
    } finally {
      setIsLoading(false);
    }
  }, [dispatch]);

  const onApply = () => {
    let filteredData = campaignTypeList;

    if (filter.campaignType?.trim()) {
      filteredData = filteredData.filter((item) =>
        item.name
          .toLowerCase()
          .includes(filter.campaignType.toLowerCase()?.trim()),
      );
    }

    setData(filteredData);
  };

  const onReset = () => {
    setFilter({
      campaignType: "",
    });
    setData(campaignTypeList);
  };

  useEffect(() => {
    getData();
  }, [getData]);

  return (
    <>
      <Card>
        <CardBody>
          <div className="mb-3 text-end">
            <Button color="primary" onClick={() => setIsOpen(true)}>
              <i className="ri-add-line me-2" />
              Create New Campaign Type
            </Button>
          </div>
          <Row>
            <Col xs={12} lg={3}>
              <FormGroup>
                <Label>Campaign Type</Label>
                <div className="form-icon right">
                  <Input
                    placeholder="Search by campaign type"
                    value={filter.campaignType}
                    onChange={(e) =>
                      setFilter({
                        ...filter,
                        campaignType: e.target.value,
                      })
                    }
                  />
                  <i className="ri-search-line" />
                </div>
              </FormGroup>
            </Col>
            <Col xs={12} lg={3} className="mt-auto">
              <FormGroup>
                <Button color="primary" onClick={onApply}>
                  Apply
                </Button>
                <Button color="light" className="ms-2" onClick={onReset}>
                  Reset
                </Button>
              </FormGroup>
            </Col>
          </Row>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <h4 className="card-title mb-0 flex-grow-1">Campaign Type List</h4>
        </CardHeader>
        <CardBody>
          <TableContainer
            columns={columns || []}
            data={data || []}
            customPageSize={10}
            isLoading={isLoading}
            tableClass="table-centered align-middle table-nowrap mb-0"
            theadClass="text-muted table-light"
            divClass="table-responsive"
          />
        </CardBody>
      </Card>

      {isOpen && (
        <CreateOrEditCampaignType
          isOpen={isOpen}
          selectedId={selectedId as number}
          onSuccess={() => getData()}
          toggle={() => {
            setIsOpen(false);
            setSelectedId(null);
          }}
        />
      )}

      {isDelete && (
        <DeleteCampaignType
          isOpen={isDelete}
          selectedId={selectedId as number}
          onSuccess={() => getData()}
          toggle={() => {
            setIsDelete(false);
            setSelectedId(null);
          }}
        />
      )}
    </>
  );
};
